import { get, isEqual, pick, set } from 'lodash';
import { ACCOUNT_ID, DIMENSION } from 'hooks';
import { isProgrammaticAccount } from 'modules/account-management/accountType';
import { campaignNameValidations } from 'modules/campaigns/config';
import { CAMPAIGNS_ROUTE, ENTITY } from 'modules/campaigns/config/routes';
import { REPORT_ENTITY_IDS } from 'modules/campaigns/hooks/useReportEntityIds';
import { REPORT_ENTITY_TYPE } from 'modules/campaigns/hooks/useReportEntityType';
import { setSelectedRows } from 'modules/campaigns/modules/campaigns-reports/actions';
import { duplicateCampaign, saveCampaign } from 'modules/campaigns/modules/campaigns-reports/flows';
import { normalizeColumnDefs } from 'modules/campaigns/modules/campaigns-reports/utils';
import { SPENDING_LIMITS } from 'modules/campaigns/modules/common-campaign-form/config/spendingLimits';
import { hasCampaignCarousel } from 'modules/campaigns/modules/creative-creator/components/CarouselCardList/utils/hasCampaignCarousel';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { PERMISSIONS_OPERATORS } from 'modules/taboola-common-frontend-modules/authentication';
import {
    CampaignActionsCellRenderer,
    CampaignStatusRenderer,
    CheckboxCellRenderer,
    CheckboxHeaderRenderer,
    FormattedMessageHeaderRenderer,
    FormattedMessageWithTooltipHeaderRenderer,
    formattedNumberValueGetter,
    gridNumberFormatter,
    StateCellRenderer,
    withCampaignEditIndication,
    CampaignNameCellRenderer,
} from '../../../components';
import { DATE_PRESET, END_DATE, REPORT_ID, START_DATE } from '../../../hooks';
import {
    actionsColumnType,
    checkBoxSelectionColumn,
    editableColumn,
    keyColumn,
    leftPinnedColumn,
    lockedColumn,
    numericColumn,
    rightPinnedColumn,
    staticColumn,
    toggleColumn,
} from '../../columns';
import EMPTY_FIELD_VALUE from '../../defaultValues';
import * as FIELDS from '../../fields';
import { nonDefaultHeaderComponentParams } from '../../fields';
import {
    isBidEditable,
    isBudgetEditable,
    isCampaignEditable,
    isCpaGoalEditable,
    isCpcCapEditable,
    isSpendingLimitEditable,
    onlyNetworkColumn,
} from '../../predicates';
import { CREATIVE_REPORT_ID } from '../creative/creativeReportId';
import { HIERARCHY_CAMPAIGN_REPORT_ID } from '../hierarchy-campaign/hierarchyCampaignReportId';
import { CAMPAIGN_REPORT_ID } from './campaignReportId';

const isDisabled = data => !isCampaignEditable({ data });

const campaignReportColumnDefinition = [
    ...[
        {
            headerComponent: CheckboxHeaderRenderer,
            headerName: FIELDS.IS_SELECTED.label,
            field: FIELDS.IS_SELECTED.field,
            type: [leftPinnedColumn, checkBoxSelectionColumn, staticColumn],
            cellRendererParams: {
                onClick: setSelectedRows,
                isDisabled,
            },
            headerComponentParams: {
                onClick: setSelectedRows,
                isDisabled,
            },
            cellRenderer: CheckboxCellRenderer,
            csv: false,
            permissions: {
                permissions: ['CAMPAIGNS_BULK_UPDATE', 'CAMPAIGNS_BULK_UPDATE_NON_ATOMIC'],
                operator: PERMISSIONS_OPERATORS.AND,
            },
            accountConfigurations: {
                [FEATURE_FLAGS.CAMPAIGNS_BULK_UPDATE_API_ENABLED_KEY]: 'true',
            },
        },
        {
            headerName: FIELDS.IS_ACTIVE.label,
            field: FIELDS.IS_ACTIVE.field,
            type: [toggleColumn, leftPinnedColumn, staticColumn, lockedColumn],
            cellRendererParams: {
                ...FIELDS.IS_ACTIVE.renderParams,
                isDisabled,
                reportId: CAMPAIGN_REPORT_ID,
            },
            cellRenderer: StateCellRenderer,
            csv: false,
        },
        {
            headerName: FIELDS.ID.label,
            field: FIELDS.ID.field,
            type: [numericColumn],
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: nonDefaultHeaderComponentParams(CAMPAIGN_REPORT_ID),
        },
        {
            headerName: FIELDS.DEMAND_TYPE.label,
            field: FIELDS.DEMAND_TYPE.field,
            cellRendererParams: {
                msgIdPrefix: 'app.campaigns.campaign.demand.type',
            },
            valueFormatter: FIELDS.DEMAND_TYPE.valueFormatter,
            csvValueGetter: FIELDS.DEMAND_TYPE.csvValueGetter,
            visibilityCondition: ({ selectedAccount }) => isProgrammaticAccount(selectedAccount),
            type: [leftPinnedColumn],
        },
        {
            headerName: FIELDS.NAME.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.NAME.headerComponentParams,
            field: FIELDS.NAME.field,
            type: [leftPinnedColumn, keyColumn, editableColumn],
            editable: isCampaignEditable,
            cellRenderer: CampaignNameCellRenderer,
            cellRendererParams: {
                lines: 2,
                to: CAMPAIGNS_ROUTE,
                paramsToKeep: [ACCOUNT_ID, DIMENSION, START_DATE, END_DATE, DATE_PRESET],
                paramsFactory: ({ id, campaignItemType }) => {
                    if (hasCampaignCarousel({ campaignItemType })) {
                        return {
                            [REPORT_ID]: HIERARCHY_CAMPAIGN_REPORT_ID,
                            [REPORT_ENTITY_TYPE]: ENTITY.CAMPAIGN,
                            [REPORT_ENTITY_IDS]: id,
                        };
                    }
                    return {
                        [REPORT_ID]: CREATIVE_REPORT_ID,
                        [REPORT_ENTITY_TYPE]: ENTITY.CAMPAIGN,
                        [REPORT_ENTITY_IDS]: id,
                    };
                },
                isSearchable: true,
            },
            cellEditor: FIELDS.NAME.cellEditor,
            cellEditorParams: {
                validations: campaignNameValidations,
                editableTextClassName: 'with-formatted-spaces',
            },
        },
        {
            headerName: FIELDS.STATUS.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.STATUS.headerComponentParams,
            field: FIELDS.STATUS.field,
            type: [leftPinnedColumn],
            cellRenderer: CampaignStatusRenderer,
            width: 180,
            minWidth: 56,
            cellRendererParams: { ...FIELDS.STATUS.renderParams, entityType: ENTITY.CAMPAIGN },
            csvValueGetter: FIELDS.STATUS.csvValueGetter,
            // TODO: DEV-71981 change valueGetter/equals once a generic solution is implemented
            valueGetter: ({ data }) =>
                pick(data, [
                    'status',
                    'isActive',
                    'creativeTrafficAllocationMethod',
                    'creativeTrafficAllocationABTestEndDate',
                    'bidStrategy',
                ]),
            equals: isEqual,
        },
        {
            headerName: FIELDS.ACCOUNT_ID.label,
            field: FIELDS.ACCOUNT_ID.field,
            visibilityCondition: onlyNetworkColumn,
        },
        {
            headerName: FIELDS.ACCOUNT_DESCRIPTION.label,
            field: FIELDS.ACCOUNT_DESCRIPTION.field,
            cellRenderer: FIELDS.ACCOUNT_DESCRIPTION.cellRenderer,
            cellRendererParams: FIELDS.ACCOUNT_DESCRIPTION.cellRendererParams,
            visibilityCondition: onlyNetworkColumn,
        },
        {
            headerName: FIELDS.CAMPAIGNS_GROUP_ID.label,
            field: FIELDS.CAMPAIGNS_GROUP_ID.field,
            type: [numericColumn],
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.CAMPAIGNS_GROUP_ID.headerComponentParams,
            accountConfigurations: { [FEATURE_FLAGS.CAMPAIGN_GROUP_ENABLED]: 'true' },
            valueFormatter: ({ value }) => value || EMPTY_FIELD_VALUE,
            cellRendererParams: FIELDS.CAMPAIGNS_GROUP_ID.renderParams,
        },
        {
            headerName: FIELDS.CAMPAIGNS_GROUP_NAME.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.CAMPAIGNS_GROUP_NAME.headerComponentParams,
            field: FIELDS.CAMPAIGNS_GROUP_NAME.field,
            cellRenderer: FIELDS.CAMPAIGNS_GROUP_NAME.valueFormatter,
            cellRendererParams: FIELDS.CAMPAIGNS_GROUP_NAME.renderParams,
            accountConfigurations: { [FEATURE_FLAGS.CAMPAIGN_GROUP_ENABLED]: 'true' },
        },
        {
            headerName: FIELDS.CAMPAIGN_CONVERSION_RULES.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.CAMPAIGN_CONVERSION_RULES.headerComponentParams,
            field: FIELDS.CAMPAIGN_CONVERSION_RULES.field,
            cellRenderer: FIELDS.CAMPAIGN_CONVERSION_RULES.cellRenderer,
            valueFormatter: FIELDS.CAMPAIGN_CONVERSION_RULES.valueFormatter,
            accountConfigurations: FIELDS.CAMPAIGN_CONVERSION_RULES.accountConfigurations,
        },
        {
            headerName: FIELDS.BUDGET.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.BUDGET.headerComponentParams,
            field: FIELDS.BUDGET.field,
            type: [numericColumn, editableColumn],
            editable: isBudgetEditable,
            cellRenderer: FIELDS.BUDGET.cellRenderer,
            cellRendererParams: FIELDS.BUDGET.renderParams,
            csv: FIELDS.BUDGET.csv,
            cellEditor: FIELDS.BUDGET.cellEditor,
            cellEditorParams: {
                fieldPath: 'budget.value',
            },
            valueGetter: 'data.budget.value',
            valueSetter: params => set(params, 'data.budget.value', +params.newValue),
        },
        {
            headerName: FIELDS.SPENDING_LIMIT.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.SPENDING_LIMIT.headerComponentParams,
            field: FIELDS.SPENDING_LIMIT.field,
            dataField: FIELDS.BUDGET.field,
            type: [editableColumn, numericColumn],
            editable: isSpendingLimitEditable,
            cellRenderer: FIELDS.SPENDING_LIMIT.cellRenderer,
            cellRendererParams: FIELDS.SPENDING_LIMIT.renderParams,
            csv: FIELDS.SPENDING_LIMIT.csv,
            cellEditor: FIELDS.SPENDING_LIMIT.cellEditor,
            valueGetter: 'data.budget.spendingLimit.value',
            cellEditorParams: {
                fieldPath: 'budget.spendingLimit.value',
            },
            valueSetter: params => set(params, 'data.budget.spendingLimit.value', +params.newValue),
        },
        {
            headerName: FIELDS.BID.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.BID.headerComponentParams,
            field: FIELDS.BID.field,
            type: [editableColumn, numericColumn],
            editable: isBidEditable,
            cellRenderer: FIELDS.BID.cellRenderer,
            cellRendererParams: FIELDS.BID.renderParams,
            cellEditor: FIELDS.BID.cellEditor,
            valueParser: ({ newValue }) => Number(newValue),
        },
        {
            headerName: FIELDS.CPA_GOAL.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.CPA_GOAL.headerComponentParams,
            field: FIELDS.CPA_GOAL.field,
            type: [editableColumn, numericColumn],
            editable: isCpaGoalEditable,
            cellRenderer: FIELDS.CPA_GOAL.cellRenderer,
            cellRendererParams: FIELDS.CPA_GOAL.renderParams,
            cellEditor: FIELDS.CPA_GOAL.cellEditor,
            valueParser: ({ newValue }) => Number(newValue),
        },
        {
            headerName: FIELDS.CPC_CAP.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.CPC_CAP.headerComponentParams,
            field: FIELDS.CPC_CAP.field,
            type: [editableColumn, numericColumn],
            editable: isCpcCapEditable,
            cellRenderer: FIELDS.CPC_CAP.cellRenderer,
            cellRendererParams: FIELDS.CPC_CAP.renderParams,
            cellEditor: FIELDS.CPC_CAP.cellEditor,
            valueParser: ({ newValue }) => Number(newValue),
        },
        {
            headerName: FIELDS.START_DATE.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.START_DATE.headerComponentParams,
            field: FIELDS.START_DATE.field,
            valueFormatter: FIELDS.START_DATE.valueFormatter,
            csvValueGetter: FIELDS.START_DATE.csvValueGetter,
        },
        {
            headerName: FIELDS.END_DATE.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.END_DATE.headerComponentParams,
            field: FIELDS.END_DATE.field,
            type: [editableColumn],
            editable: isCampaignEditable,
            valueFormatter: FIELDS.END_DATE.valueFormatter,
            csvValueGetter: FIELDS.END_DATE.csvValueGetter,
            cellEditor: FIELDS.END_DATE.cellEditor,
            cellEditorParams: {
                placeholderMsgId: 'campaign.time.frame.no.end.date',
                defaultPlaceholderMsg: 'No End Date',
                inputIconMsgId: 'campaign.time.frame.end',
                defaultInputIconMsg: 'End',
            },
        },
        {
            headerName: FIELDS.BID_STRATEGY.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.BID_STRATEGY.headerComponentParams,
            field: FIELDS.BID_STRATEGY.field,
            cellRenderer: FIELDS.BID_STRATEGY.cellRenderer,
            csvValueGetter: FIELDS.BID_STRATEGY.csvValueGetter,
        },
        {
            headerName: FIELDS.SAFETY_LEVEL.label,
            headerValueGetter: FIELDS.SAFETY_LEVEL.headerValueGetter,
            headerComponentParams: FIELDS.SAFETY_LEVEL.headerComponentParams,
            field: FIELDS.SAFETY_LEVEL.field,
            cellRenderer: FIELDS.SAFETY_LEVEL.cellRenderer,
            cellRendererParams: FIELDS.SAFETY_LEVEL.renderParams,
            csvValueGetter: FIELDS.SAFETY_LEVEL.csvValueGetter,
        },
        {
            headerName: FIELDS.PACING_SCORE.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.PACING_SCORE.headerComponentParams,
            field: FIELDS.PACING_SCORE.field,
            type: [numericColumn],
            cellRenderer: FIELDS.PACING_SCORE.cellRenderer,
            cellRendererParams: FIELDS.PACING_SCORE.renderParams,
        },
    ].map(colDef => ({ valueChangeHandler: saveCampaign, ...colDef })),
    ...[
        FIELDS.ESTIMATED_DAILY_CAP,
        FIELDS.VISIBLE_IMPRESSIONS,
        FIELDS.CLICKS,
        FIELDS.CONVERSIONS,
        FIELDS.CONVERSIONS_CLICKS,
        FIELDS.CONVERSIONS_VIEWS,
        FIELDS.CPA,
        FIELDS.CPA_CLICKS,
        FIELDS.CPA_VIEWS,
        FIELDS.CVR,
        FIELDS.CVR_CLICKS,
        FIELDS.CVR_VIEWS,
        FIELDS.AVERAGE_CPC,
        FIELDS.VCTR,
        FIELDS.VCPM,
        FIELDS.IMPRESSIONS,
        FIELDS.UNIQUE_IMPRESSIONS,
        FIELDS.UNIQUE_VIEWABLE_IMPRESSIONS,
        FIELDS.UNIQUE_CLICKS,
        FIELDS.PROFIT_MARGIN,
        FIELDS.ESTIMATED_BILLABLE_AMOUNT,
        {
            ...FIELDS.SPENT,
            renderParams: {
                ...FIELDS.SPENT.renderParams,
                barVisibilityCondition: data => get(data, 'budget.type') === SPENDING_LIMITS.LIFETIME,
            },
        },
        FIELDS.CONVERSIONS_VALUE,
        FIELDS.ROAS,
        FIELDS.ROAS_CLICKS,
        FIELDS.ROAS_VIEWS,
    ].map(fieldConfig => ({
        headerName: fieldConfig.label,
        headerValueGetter: fieldConfig.headerValueGetter || FormattedMessageWithTooltipHeaderRenderer,
        headerComponentParams: fieldConfig.headerComponentParams,
        field: fieldConfig.field,
        type: [numericColumn],
        cellRenderer: fieldConfig.cellRenderer,
        valueFormatter: !fieldConfig.cellRenderer && gridNumberFormatter,
        cellRendererParams: fieldConfig.renderParams,
        csvValueGetter: fieldConfig.csvValueGetter || formattedNumberValueGetter,
        referenceColId: fieldConfig.referenceColId,
        accountConfigurations: fieldConfig.accountConfigurations,
        permissions: fieldConfig.permissions,
        visibilityCondition: fieldConfig.visibilityCondition,
    })),
    {
        headerName: FIELDS.ACTIONS_COLUMN.label,
        field: FIELDS.ACTIONS_COLUMN.field,
        valueGetter: params => params.data,
        type: [rightPinnedColumn, actionsColumnType, staticColumn],
        cellRendererParams: {
            onDuplicate: duplicateCampaign,
            onTogglePolicyState: saveCampaign,
        },
        cellRenderer: CampaignActionsCellRenderer,
        csv: false,
        minWidth: 100,
        maxWidth: 150,
        width: 150,
    },
];

export default normalizeColumnDefs(campaignReportColumnDefinition, CAMPAIGN_REPORT_ID, withCampaignEditIndication);
